import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { getCurrentUser } from '../../publicSession/selectors';
import { getGrusername } from '../selectors';
import { selectors, withOffset } from '../../Stream';
import { DeviationTypes } from '../../types/deviation';
import { fetch, fakeResponseForType } from './helpers';
import {
  MEDIA_POPUP_POLL_STREAM_ID,
  MEDIA_POPUP_STATUS_STREAM_ID,
  MEDIA_POPUP_JOURNAL_STREAM_ID,
  MEDIA_POPUP_POSTS_STREAM_ID,
} from '../constants';
import type { FetchAction } from '../../Stream/base/types';
import type { PapiDaSharedPostsFetch } from '@wix/da-papi-types';
import { normalizeDeviations } from '../../redux/normalizr/helpers';
import { FetchType } from '../../Stream/base/types';

const streamIdToDeviationTypeMap = {
  [MEDIA_POPUP_POLL_STREAM_ID]: DeviationTypes.POLL,
  [MEDIA_POPUP_STATUS_STREAM_ID]: DeviationTypes.STATUS,
  [MEDIA_POPUP_JOURNAL_STREAM_ID]: DeviationTypes.JOURNAL,
  [MEDIA_POPUP_POSTS_STREAM_ID]: 'all_posts',
};

function* handlePostStreamFetch(action: FetchAction): SagaIterator {
  const { streamId } = action;
  const type = streamIdToDeviationTypeMap[streamId];

  const {
    offset,
    limit,
    username,
    currentUser,
  }: {
    currentUser: ReturnType<typeof getCurrentUser>;
    username: ReturnType<typeof getGrusername>;
    offset: ReturnType<typeof withOffset.selectors.getNextOffset>;
    limit: ReturnType<typeof selectors.getItemsPerFetch>;
  } = yield select(state => ({
    currentUser: getCurrentUser(state),
    username: getGrusername(state),
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
  }));

  // TODO: update PAPI type once endpoint is created
  const params = {
    type: type === 'all_posts' ? 'all' : type,
    username: username || currentUser.username,
    offset,
    limit,
  };

  const fakeResponse = fakeResponseForType(type);
  const response: PapiDaSharedPostsFetch | false = yield call(fetch, {
    url: 'posts/fetch',
    params,
    fakeResponse,
  });

  if (!response) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch',
      })
    );
    return;
  }

  const { hasMore, results } = response;
  const normalized = results && normalizeDeviations(results);

  yield put(
    withOffset.actions.fetchSuccess({
      streamId,
      hasMore: results ? !!hasMore : false,
      items: normalized?.result ?? [],
      entities: normalized?.entities ?? {},
    })
  );
}

export default handlePostStreamFetch;
